import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminGroup, AdminMember } from '@/lib/api'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 160
}

export default function GroupsPage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()

  const [items, setItems] = useState<AdminGroup[]>([])
  const [membersByGroup, setMembersByGroup] = useState<Record<string, AdminMember[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const [groups, members] = await Promise.all([
        api.getAdminGroups(token),
        api.getAdminMembers(token),
      ])

      if (isGroupManager && managedGroupSlug) {
        const myGroup = groups.find((g) => g.slug === managedGroupSlug)
        if (myGroup) {
          navigate(`/groups/${myGroup.id}`, { state: { group: myGroup }, replace: true })
          return
        }
      }

      setItems(groups)
      const byGroup: Record<string, AdminMember[]> = {}
      for (const m of members) {
        ;(byGroup[m.group_slug] ??= []).push(m)
      }
      setMembersByGroup(byGroup)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string, name: string) => {
    if (!await confirm(`"${name}" dan semua data terkait akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${name}"` })) return
    if (!token) return
    try {
      await api.deleteGroup(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Groups</h1>
          <p className="text-zinc-500 text-xs mt-0.5">{items.length} groups</p>
        </div>
        {!isGroupManager && (
          <button
            onClick={() => navigate('/groups/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Group
          </button>
        )}
      </div>

      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">No groups yet.</p>
          {!isGroupManager && (
            <button
              onClick={() => navigate('/groups/create')}
              className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              + Add the first one
            </button>
          )}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((group) => {
            const groupMembers = membersByGroup[group.slug] ?? []
            const light = isLightColor(group.color_primary || '#3f3f46')
            return (
              <div
                key={group.id}
                className="relative rounded-xl overflow-hidden cursor-pointer h-44 hover:scale-[1.02] transition-transform"
                style={{
                  backgroundColor: group.color_primary || '#3f3f46',
                  ...(group.cover_image_url && {
                    backgroundImage: `url(${normalizeImageKitUrl(group.cover_image_url)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }),
                }}
                onClick={() => navigate(`/groups/${group.id}`, { state: { group } })}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background: group.cover_image_url
                      ? `linear-gradient(to top, ${group.color_primary}dd, ${group.color_primary}44, transparent)`
                      : 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                  }}
                />

                <div
                  className="absolute top-2 right-2 z-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RowActions
                    triggerClassName="flex items-center justify-center w-7 h-7 rounded bg-black/20 hover:bg-black/40 text-white/80 hover:text-white transition-colors"
                    actions={[
                      { label: 'Edit', onClick: () => navigate(`/groups/${group.id}/edit`, { state: { group } }) },
                      { label: 'Lihat Members', onClick: () => navigate(`/members?group=${group.slug}`) },
                      ...(!isGroupManager ? [{ label: 'Hapus', onClick: () => handleDelete(group.id, group.name), variant: 'danger' as const }] : []),
                    ]}
                  />
                </div>

                <div className="relative h-full flex flex-col justify-between p-4">
                  {groupMembers.length > 0 && (
                    <div className="flex -space-x-2">
                      {groupMembers.slice(0, 5).map((m) => (
                        <img
                          key={m.id}
                          src={normalizeImageKitUrl(m.image_url)}
                          alt={m.stage_name}
                          className="w-7 h-7 rounded-full border-2 border-white/30 object-cover bg-zinc-700"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                      ))}
                      {groupMembers.length > 5 && (
                        <div className="w-7 h-7 rounded-full bg-black/40 border-2 border-white/30 flex items-center justify-center text-[10px] font-medium text-white">
                          +{groupMembers.length - 5}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <h3 className={`font-semibold text-base leading-tight ${light ? 'text-zinc-900' : 'text-white'}`}>
                      {group.name}
                    </h3>
                    <p className={`text-xs mt-0.5 truncate ${light ? 'text-zinc-700' : 'text-white/70'}`}>
                      {group.tagline}
                    </p>
                    <p className={`text-[10px] mt-1.5 ${light ? 'text-zinc-600' : 'text-white/50'}`}>
                      {groupMembers.length} members · Debut {group.debut_date}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {dialog}
    </div>
  )
}
